import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, Search, UserRound, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { UserAvatar } from "@/components/common";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useTeam, searchTeam, displayName } from "@/hooks/useTeam";

/** A person or department as a compact chip: avatar/initials + name. Departments get a square-ish mark. */
export function PersonChip({ person, size = "sm", className, testId, hideName = false }) {
  if (!person) return null;
  const dept = person.kind === "dept";
  return (
    <span className={cn("inline-flex min-w-0 items-center gap-1.5 text-[12.5px] text-foreground/85", className)} data-testid={testId} title={`${displayName(person)}${person.kind === "member" ? " · uten innlogging" : dept ? " · avdeling" : ""}`}>
      {dept ? <span className={cn("inline-flex shrink-0 items-center justify-center rounded-[6px] font-mono font-semibold text-white", size === "xs" ? "h-5 w-5 text-[9px]" : "h-6 w-6 text-[10px]")} style={{ backgroundColor: person.color || "#3b5b7a" }}>{person.code || person.initials}</span>
        : <UserAvatar name={person.name} initials={person.initials} color={person.color} src={person.avatar_url} size={size === "xs" ? "xs" : "sm"} />}
      {!hideName ? <span className="truncate">{displayName(person)}</span> : null}
    </span>
  );
}

function Option({ e, active, onPick, me }) {
  return (
    <button type="button" role="option" aria-selected={active} onClick={() => onPick(e)} className={cn("flex w-full items-center gap-2.5 rounded-[9px] px-2 py-1.5 text-left text-[13px] outline-none transition-colors hover:bg-black/[0.045]", active && "bg-[hsl(var(--brand-soft))]")} data-testid={`team-option-${e.id}`}>
      <PersonChip person={e} hideName />
      <span className="min-w-0 flex-1 leading-tight">
        <span className="block truncate font-medium">{displayName(e)}{e.id === me ? <span className="ml-1 text-[11px] font-normal text-muted-foreground">(deg)</span> : null}</span>
        <span className="block truncate text-[11.5px] text-muted-foreground">{e.kind === "dept" ? "Avdeling" : e.kind === "member" ? `${e.title || "Teammedlem"} · uten innlogging` : e.title || "Bruker"}</span>
      </span>
      {active ? <Check className="h-3.5 w-3.5 shrink-0 text-primary" strokeWidth={2.6} /> : null}
    </button>
  );
}

/** Searchable list of the whole directory – used by the assignee picker and the @-mention suggestions. */
export function TeamList({ query, onPick, value, autoFocus = false, onQuery, testId = "team-list", limit = 8 }) {
  const { all, me, loading } = useTeam();
  const list = useMemo(() => searchTeam(all, query).slice(0, limit), [all, query, limit]);
  return (
    <div data-testid={testId}>
      {onQuery ? (
        <label className="mb-1.5 flex h-8 items-center gap-2 rounded-[9px] border border-black/[0.1] bg-white px-2.5 text-[13px] focus-within:border-primary/50">
          <Search className="h-3.5 w-3.5 text-muted-foreground" />
          <input autoFocus={autoFocus} value={query} onChange={(e) => onQuery(e.target.value)} placeholder="Søk person eller avdeling…" className="min-w-0 flex-1 bg-transparent outline-none placeholder:text-muted-foreground/60" aria-label="Søk i teamet" data-testid={`${testId}-search`} />
        </label>
      ) : null}
      <div role="listbox" className="max-h-[260px] space-y-0.5 overflow-y-auto scrollbar-thin">
        {loading && !all.length ? <div className="px-2 py-2 text-[12.5px] text-muted-foreground">Laster teamet…</div> : null}
        {list.map((e) => <Option key={e.id} e={e} active={value?.id === e.id} onPick={onPick} me={me} />)}
        {!loading && !list.length ? <div className="px-2 py-2 text-[12.5px] text-muted-foreground">Ingen treff. Teammedlemmer legges til under Innstillinger → Team.</div> : null}
      </div>
    </div>
  );
}

/** «Prises av»: pick a person or department for a work item, or clear it. Saves through onChange(ref|null). */
export function AssigneePicker({ value, onChange, dueDate, onDueDate, testId = "assignee", compact = false }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  useEffect(() => { if (!open) setQ(""); }, [open]);
  return (
    <span className="inline-flex max-w-full items-center gap-1.5" data-testid={testId}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" className={cn("inline-flex h-7 max-w-[240px] items-center gap-1.5 rounded-full border px-2 text-[12.5px] transition-colors", value ? "border-black/[0.1] bg-white hover:border-black/[0.25]" : "border-dashed border-black/[0.18] text-muted-foreground hover:border-black/[0.35] hover:text-foreground")} data-testid={`${testId}-button`} aria-haspopup="listbox" aria-expanded={open}>
            {value ? <PersonChip person={value} size="xs" /> : <><UserRound className="h-3.5 w-3.5" /> {compact ? "Tildel" : "Prises av…"}</>}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" sideOffset={6} className="w-[300px] rounded-[14px] border-black/[0.06] p-2 shadow-float" data-testid={`${testId}-popover`}>
          <TeamList query={q} onQuery={setQ} autoFocus value={value} onPick={(e) => { onChange(e.id === value?.id ? null : e); setOpen(false); }} testId={`${testId}-list`} />
          {value ? <button type="button" onClick={() => { onChange(null); setOpen(false); }} className="mt-1 flex h-8 w-full items-center gap-2 rounded-[9px] px-2 text-[12.5px] text-muted-foreground hover:bg-black/[0.045] hover:text-foreground" data-testid={`${testId}-clear`}><X className="h-3.5 w-3.5" /> Fjern tildeling</button> : null}
        </PopoverContent>
      </Popover>
      {onDueDate && (value || dueDate) ? (
        <input type="date" value={dueDate || ""} onChange={(e) => onDueDate(e.target.value || null)} className={cn("h-7 rounded-full border border-black/[0.1] bg-white px-2 text-[12px] text-foreground/80 outline-none hover:border-black/[0.25] focus-visible:ring-2 focus-visible:ring-primary/15", !dueDate && "text-muted-foreground")} aria-label="Frist" title="Frist for prising" data-testid={`${testId}-due`} />
      ) : null}
    </span>
  );
}

/** Floating @-mention suggestions above a textarea. Arrow keys + Enter handled by the parent via `index`. */
export function MentionMenu({ query, index, onPick, testId = "mention-menu" }) {
  const { all } = useTeam();
  const list = useMemo(() => searchTeam(all, query).slice(0, 6), [all, query]);
  const ref = useRef(null);
  useEffect(() => { ref.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: "nearest" }); }, [index]);
  if (!list.length) return null;
  return (
    <div ref={ref} role="listbox" className="absolute bottom-full left-3 right-3 z-30 mb-2 max-h-[240px] overflow-y-auto rounded-[14px] border border-black/[0.08] bg-white p-1.5 shadow-float" data-testid={testId}>
      <div className="px-2 pb-1 text-[11px] text-muted-foreground">Nevn i notatet</div>
      {list.map((e, i) => <Option key={e.id} e={e} active={i === index} onPick={onPick} />)}
    </div>
  );
}

export const mentionCandidates = (all, q) => searchTeam(all, q).slice(0, 6);

const dueText = (iso) => {
  if (!iso) return null;
  const d = Math.round((new Date(iso) - new Date(new Date().toDateString())) / 86400000);
  return d < 0 ? `frist passert ${-d} d` : d === 0 ? "frist i dag" : d === 1 ? "frist i morgen" : `frist om ${d} d`;
};

/** Read-only mark on a list row: who prices this post and when it is due. Amber when the date has passed and the post is still open. */
export function AssignedMark({ item, className, testId }) {
  const a = item?.assigned_to;
  if (!a) return null;
  const open = ["venter_pris", "ma_vurderes"].includes(item.calc?.state);
  const late = open && item.due_date && item.due_date < new Date().toISOString().slice(0, 10);
  return (
    <span className={cn("inline-flex max-w-full items-center gap-1.5 text-[11.5px]", late ? "text-[hsl(var(--v-assumed))]" : "text-muted-foreground", className)} title={`Prises av ${displayName(a)}${item.due_date ? ` · ${dueText(item.due_date)}` : ""}${open ? "" : " · ferdig"}`} data-testid={testId} data-late={late || undefined}>
      <PersonChip person={a} size="xs" hideName />
      <span className="truncate">{displayName(a)}{item.due_date ? <span className="ml-1 opacity-80">· {dueText(item.due_date)}</span> : null}</span>
    </span>
  );
}
